// TelcoTrack — BUDOVA odbočka origin at the first (junction) vertex.

export const BUDOVA_BRANCH_ORIGIN_KIND = {
  NEW_SPOJKOVISTE: 'NEW_SPOJKOVISTE',
  SHARED_SPOJKOVISTE: 'SHARED_SPOJKOVISTE',
  ROZVADEC: 'ROZVADEC',
} as const;

export type BudovaBranchOriginKind =
  (typeof BUDOVA_BRANCH_ORIGIN_KIND)[keyof typeof BUDOVA_BRANCH_ORIGIN_KIND];

/** First vertex “already has another building’s spojkoviště” — at the snapped point. */
export const BUDOVA_BRANCH_EXISTING_SPOJKOVISTE_MAX_M = 2;

export const BUDOVA_BRANCH_ORIGIN_META_KEYS = {
  branchOriginKind: 'branchOriginKind',
  branchRozvadecId: 'branchRozvadecId',
} as const;

export function isBudovaBranchOriginKind(
  value: string | null | undefined,
): value is BudovaBranchOriginKind {
  const k = (value ?? '').trim().toUpperCase();
  return (Object.values(BUDOVA_BRANCH_ORIGIN_KIND) as string[]).includes(k);
}

export function readBudovaBranchOriginKind(
  metadata: Record<string, unknown> | null | undefined,
): BudovaBranchOriginKind {
  const meta = metadata ?? {};
  const custom = (meta['customFields'] ?? meta['custom_fields']) as Record<string, unknown> | undefined;
  const raw = custom?.[BUDOVA_BRANCH_ORIGIN_META_KEYS.branchOriginKind] ?? meta[BUDOVA_BRANCH_ORIGIN_META_KEYS.branchOriginKind];
  const k = typeof raw === 'string' ? raw.trim().toUpperCase() : '';
  return isBudovaBranchOriginKind(k) ? k : BUDOVA_BRANCH_ORIGIN_KIND.NEW_SPOJKOVISTE;
}

export function readBudovaBranchRozvadecId(
  metadata: Record<string, unknown> | null | undefined,
): string | null {
  const meta = metadata ?? {};
  const custom = (meta['customFields'] ?? meta['custom_fields']) as Record<string, unknown> | undefined;
  const raw = custom?.[BUDOVA_BRANCH_ORIGIN_META_KEYS.branchRozvadecId] ?? meta[BUDOVA_BRANCH_ORIGIN_META_KEYS.branchRozvadecId];
  return sanitizeOptionalObjectId(typeof raw === 'string' ? raw : null) ?? null;
}

/**
 * `objects.id` is varchar(50) alphanumeric (not UUID).
 * For optional draw fields: omit empty / whitespace instead of sending `""`.
 */
export function sanitizeOptionalObjectId(value: string | null | undefined): string | undefined {
  const id = (value ?? '').trim();
  return id || undefined;
}

/** NEW + SHARED spojkoviště: Odbočeno needs the junction step (incl. this building’s MT barvy). ROZVADEC: výkop only. */
export function odbocenoRequiresJunctionSpojkoviste(
  kind: BudovaBranchOriginKind | null | undefined,
): boolean {
  const k = kind ?? BUDOVA_BRANCH_ORIGIN_KIND.NEW_SPOJKOVISTE;
  return k !== BUDOVA_BRANCH_ORIGIN_KIND.ROZVADEC;
}

export function odbocenoRequiresNapojeneMtBarvy(
  kind: BudovaBranchOriginKind | null | undefined,
): boolean {
  return odbocenoRequiresJunctionSpojkoviste(kind);
}

export interface BudovaBranchDrawOriginInput {
  currentBudovaId: string;
  /** Nearby junction SPOJKOVISTE already linked to a *different* BUDOVA (nearest first). */
  junctionSpojkovisteOfOtherBuildings: ReadonlyArray<{ id: string }>;
  /** First vertex was a click on a ROZVADEC object (not a nearby-cabinet distance snap). */
  clickedRozvadecId?: string | null;
}

export interface BudovaBranchDrawOrigin {
  kind: BudovaBranchOriginKind;
  spojkovisteId: string | null;
  rozvadecId: string | null;
}

/**
 * First-point origin: click on a ROZVADEC object → cabinet přípojka;
 * else existing other-building spojkoviště at the route vertex → share;
 * else create a new spojkoviště. Nearby-to-cabinet must not become ROZVADEC.
 */
/** FK sloupce BUDOVA při odbočce přímo z rozvaděče (Zafouknuto z = zdrojový ROZVADEC). */
export function buildBudovaBranchRozvadecFkFields(input: {
  rozvadecId: string;
  rozvadecName?: string | null;
}): {
  pripojovanyZafouknutoZRozvadecObjectId: string;
  pripojovanyZafouknutoZRozvadecName: string;
  zafukZRozvadecObjectId: string;
  zafukZRozvadecName: string;
} {
  const id = (input.rozvadecId ?? '').trim();
  const name = (input.rozvadecName ?? '').trim() || id;
  return {
    pripojovanyZafouknutoZRozvadecObjectId: id,
    pripojovanyZafouknutoZRozvadecName: name,
    zafukZRozvadecObjectId: id,
    zafukZRozvadecName: name,
  };
}

export function resolveBudovaBranchDrawOrigin(
  input: BudovaBranchDrawOriginInput,
): BudovaBranchDrawOrigin {
  const clickedRoz = sanitizeOptionalObjectId(input.clickedRozvadecId);
  if (clickedRoz) {
    return {
      kind: BUDOVA_BRANCH_ORIGIN_KIND.ROZVADEC,
      spojkovisteId: null,
      rozvadecId: clickedRoz,
    };
  }
  const shared = input.junctionSpojkovisteOfOtherBuildings[0];
  if (shared?.id) {
    return {
      kind: BUDOVA_BRANCH_ORIGIN_KIND.SHARED_SPOJKOVISTE,
      spojkovisteId: shared.id,
      rozvadecId: null,
    };
  }
  return {
    kind: BUDOVA_BRANCH_ORIGIN_KIND.NEW_SPOJKOVISTE,
    spojkovisteId: null,
    rozvadecId: null,
  };
}
